import React, { useEffect, useState } from "react";
import {
    collection,
    query,
    orderBy,
    onSnapshot,
    doc,
    deleteDoc,
} from "firebase/firestore";
import { db, archiveOrder } from "./firebase";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const OrderCard = ({ order, onComplete }) => {
    const {
        queueNumber = 'N/A', name = "לא צוין", contact = "", items = [], totalPrice = 0, paymentMethod = "לא צוין"
    } = order;
    const isPaid = paymentMethod !== 'מזומן בקופה';
    const paymentStatusText = isPaid ? "שולם" : `לתשלום: ${totalPrice} ₪ במזומן`;
    const paymentStatusColor = isPaid ? "text-green-400" : "text-red-500";

    return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 text-right shadow-md w-full">
            <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-600">
                <div className="flex flex-col items-start">
                    <span className="text-sm text-gray-400">מספר בתור</span>
                    <span className="text-3xl font-bold text-amber-400">{queueNumber}</span>
                </div>
                <div className="text-right">
                    <h3 className="text-xl font-bold text-white">{name}</h3>
                    {contact && <p className="text-md text-gray-300">{contact}</p>}
                </div>
            </div>
            <div className="space-y-2 mb-3">
                {items.map((item, index) => (
                    <div key={index} className="bg-gray-700 p-2 rounded-md">
                        <p className="font-semibold text-white">{item.quantity}x {item.parentType === 'chamin' ? `צ'ולנט ${item.size}` : item.label}</p>
                        {item.parentType === 'chamin' && (
                            <div className="text-xs text-gray-300 pt-1 pl-2">
                                <span>{item.withMeat ? 'עם בשר' : 'בלי בשר'}</span>
                                <span className="px-1">|</span>
                                <span>{item.withKishke ? 'עם קישקע' : 'בלי קישקע'}</span>
                            </div>
                        )}
                        <div className="text-sm text-gray-400 pl-2 pt-1">
                            {item.kashrut && <p>כשרות: <span className="font-medium text-gray-300">{item.kashrut}</span></p>}
                            {item.owner && <p>שייך ל: <span className="font-medium text-gray-300">{item.owner}</span></p>}
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-gray-600">
                <div><span className={`text-lg font-bold ${paymentStatusColor}`}>{paymentStatusText}</span></div>
                <button className="bg-green-600 text-white font-bold px-5 py-2 rounded-lg hover:bg-green-700 transition-colors" onClick={() => onComplete(order)}>בוצע</button>
            </div>
        </div>
    );
};

export default function ActiveOrders() {
    const [orders, setOrders] = useState([]);
    const [userId, setUserId] = useState(null);
    const navigate = useNavigate();

    // Get current user ID
    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUserId(user ? user.uid : null);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!userId) return;
        const q = query(collection(db, "users", userId, "orders"), orderBy("timestamp"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (error) => console.error("Error fetching active orders: ", error));
        return () => unsubscribe();
    }, [userId]);

    const handleCompleteOrder = async (order) => {
        await archiveOrder(userId, order);
        await deleteDoc(doc(db, "users", userId, "orders", order.id));
    };

    return (
        <div className="bg-gray-900 min-h-screen text-white" dir="rtl">
            <header className="sticky top-0 bg-gray-900 bg-opacity-80 backdrop-blur-md z-40 shadow-lg p-4 border-b border-gray-700">
                <div className="max-w-5xl mx-auto flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-amber-400">הזמנות פעילות</h1>
                    <div className="flex items-center gap-2">
                        <button onClick={() => navigate("/")} className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-blue-700">הוסף הזמנה</button>
                        <button onClick={() => navigate("/history")} className="bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-gray-700">היסטוריית הזמנות</button>
                        <button onClick={() => navigate("/config")} className="bg-amber-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-amber-700">קונפיגורציה</button>
                    </div>
                </div>
            </header>
            <main className="p-4">
                <div className="max-w-5xl mx-auto">
                    {orders.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {orders.map((order) => (<OrderCard key={order.id} order={order} onComplete={handleCompleteOrder} />))}
                        </div>
                    ) : (
                        <div className="text-center text-gray-400 mt-20"><p className="text-xl">אין הזמנות פעילות כרגע.</p></div>
                    )}
                </div>
            </main>
        </div>
    );
} 